import React, { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, X, Search as SearchIcon } from "lucide-react";

/**
 * Multi-select filter with checkbox rows inside a shadcn Popover.
 * - Uniform h-12 trigger, rounded-full, focus-visible ring.
 * - Selected values render as a compact "N kategori · first" label to keep width stable.
 * - Contains optional search box when options list is long (>= 8).
 * - Soft shadow on dropdown so it doesn't clobber content beneath.
 *
 * Props:
 *   icon:        JSX element rendered inside the trigger (Lucide, 16px, navy)
 *   placeholder: string shown when nothing selected
 *   options:     Array of strings OR {value, label}
 *   value:       Array of selected value strings
 *   onChange:    (nextArray) => void
 *   testid:      base data-testid (also applies to trigger)
 *   maxWidthClass: class controlling trigger min-width (default min-w-[180px])
 */
export default function MultiSelectCheckbox({
  icon,
  placeholder,
  options = [],
  value = [],
  onChange,
  testid,
  maxWidthClass = "min-w-[180px]",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  // Normalize options
  const opts = useMemo(() => (options || []).map((o) =>
    typeof o === "string" ? { value: o, label: o } : o), [options]);

  const filtered = useMemo(() => {
    if (!q.trim()) return opts;
    const needle = q.trim().toLowerCase();
    return opts.filter((o) => o.label.toLowerCase().includes(needle) || o.value.toLowerCase().includes(needle));
  }, [opts, q]);

  const toggle = (v) => {
    const set = new Set(value);
    if (set.has(v)) set.delete(v); else set.add(v);
    onChange?.(Array.from(set));
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange?.([]);
  };

  const labelForSelected = () => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) return value[0];
    return `${value.length} dipilih`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={
            "h-12 " + maxWidthClass + " rounded-full border border-[#E8E4DB] bg-white " +
            "flex items-center gap-2 px-4 text-sm text-[#1F2937] hover:border-[#D2673E] hover:bg-[#FEFAF7] " +
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D2673E]/30 transition-colors"
          }
        >
          {icon && <span className="shrink-0 inline-flex items-center">{icon}</span>}
          <span className={"flex-1 min-w-0 text-left truncate " + (value.length === 0 ? "text-[#8A8F99]" : "font-medium")}>
            {labelForSelected()}
          </span>
          {value.length > 0 ? (
            <span
              role="button"
              tabIndex={-1}
              onClick={clear}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); clear(e); } }}
              data-testid={testid ? `${testid}-clear` : undefined}
              className="shrink-0 w-5 h-5 rounded-full bg-[#F6E7DF] text-[#BE5730] flex items-center justify-center hover:bg-[#E6C9BB] cursor-pointer"
              aria-label="Hapus pilihan"
            >
              <X size={11} strokeWidth={2.5} />
            </span>
          ) : (
            <ChevronDown size={14} strokeWidth={1.75} className="shrink-0 text-[#8A8F99]" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-64 p-2 rounded-2xl border-[#E8E4DB] shadow-[0_8px_24px_-8px_rgba(30,58,138,0.15)]"
        data-testid={testid ? `${testid}-menu` : undefined}
      >
        {opts.length >= 8 && (
          <div className="relative mb-2">
            <SearchIcon size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8F99]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari…"
              data-testid={testid ? `${testid}-search` : undefined}
              className="w-full h-9 pl-8 pr-3 rounded-lg bg-[#FAFAF8] border border-transparent focus:border-[#D2673E] focus:bg-white focus:outline-none text-xs"
            />
          </div>
        )}
        <div className="max-h-64 overflow-y-auto py-1">
          {filtered.length === 0 && (
            <div className="px-3 py-4 text-xs text-[#8A8F99] italic text-center">Tidak ada opsi cocok.</div>
          )}
          {filtered.map((o) => {
            const checked = value.includes(o.value);
            return (
              <label
                key={o.value}
                data-testid={testid ? `${testid}-opt-${o.value}` : undefined}
                className="flex items-center gap-2.5 px-2.5 py-2 rounded-md cursor-pointer hover:bg-[#FAFAF8] transition-colors"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggle(o.value)}
                  className="border-[#E8E4DB] data-[state=checked]:bg-[#D2673E] data-[state=checked]:border-[#D2673E]"
                />
                <span className={"text-sm text-[#1F2937] flex-1 truncate " + (checked ? "font-medium" : "")}>
                  {o.label}
                </span>
              </label>
            );
          })}
        </div>
        {value.length > 0 && (
          <div className="pt-2 mt-1 border-t border-[#F3F1ED] flex items-center justify-between px-1">
            <span className="text-[11px] text-[#8A8F99]">{value.length} dipilih</span>
            <button type="button" onClick={() => onChange?.([])}
              className="text-[11px] font-semibold text-[#BE5730] hover:underline"
              data-testid={testid ? `${testid}-clear-all` : undefined}
            >
              Hapus semua
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
